/**
 * Stock management.
 *
 * The seat count is an operator-only edit guarded by a version token: if a
 * colleague saved first, the write is refused and the screen says so instead
 * of silently discarding their correction. Adjusting delivered seats is open
 * to technicians too, exactly as in the legacy screen.
 */
import { useMemo, useState } from "react";
import { formatNumberFr, type StockRow } from "@soi/contracts";
import { ApiFailure } from "../lib/api.js";
import { useAdjustStock, useMe, usePatchStock, useStock } from "../lib/queries.js";
import { useToast } from "../components/Toast.js";

function Row({ row, readOnly }: { row: StockRow; readOnly: boolean }) {
  const [amount, setAmount] = useState(1);
  const [draft, setDraft] = useState<string>(row.realSeats?.toString() ?? "");
  const adjust = useAdjustStock();
  const patch = usePatchStock();
  const toast = useToast();

  const save = async () => {
    const value = draft.trim() === "" ? null : Number.parseInt(draft, 10);
    if (value !== null && (Number.isNaN(value) || value < 0)) {
      toast("Le nombre de postes doit être positif.", "err");
      return;
    }
    if (value === row.realSeats) return;
    try {
      await patch.mutateAsync({ slug: row.slug, realSeats: value, version: row.version });
      toast(`Postes réels enregistrés — ${row.name}`, "ok");
    } catch (err) {
      if (err instanceof ApiFailure && err.code === "CONFLIT_VERSION") {
        // Show what the other operator saved rather than overwriting it.
        const current = (err.details?.current as StockRow | undefined)?.realSeats;
        setDraft(current?.toString() ?? "");
        toast(
          `${row.name} : un collègue a enregistré ${current ?? "aucune correction"} entre-temps. ` +
            "La valeur affichée a été rafraîchie.",
          "err",
        );
        return;
      }
      toast(err instanceof ApiFailure ? err.message : "L'enregistrement a échoué.", "err");
      setDraft(row.realSeats?.toString() ?? "");
    }
  };

  const step = async (sign: 1 | -1) => {
    if (!amount || amount < 1) {
      setAmount(1);
      return;
    }
    try {
      await adjust.mutateAsync({ slug: row.slug, delta: sign * amount });
      toast(
        `${amount} poste${amount > 1 ? "s" : ""} ${sign > 0 ? "ajouté" : "retiré"}${amount > 1 ? "s" : ""} — ${row.name}`,
        sign > 0 ? "ok" : "annule",
      );
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "L'ajustement a échoué.", "err");
    }
  };

  return (
    <tr data-testid={`stock-${row.slug}`}>
      <td>{row.name}</td>
      <td>
        <span className={`tag ${row.lot === "Police" ? "pol" : "soi"}`}>
          {row.lot === "Police" ? "Police" : "SOI"}
        </span>
      </td>
      <td>{row.zone}</td>
      <td>{formatNumberFr(row.plannedSeats)}</td>
      <td>
        <input
          type="number"
          min={0}
          style={{ width: 90 }}
          placeholder={String(row.plannedSeats)}
          value={draft}
          disabled={readOnly}
          aria-label={`Postes réels · ${row.name}`}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void save()}
        />
      </td>
      <td className="livres" data-testid={`delivered-${row.slug}`}>
        {formatNumberFr(row.delivered)}
      </td>
      <td>
        <span className="ajuste">
          <button
            type="button"
            className="pas moins"
            aria-label={`Retirer des postes livrés · ${row.name}`}
            disabled={adjust.isPending}
            onClick={() => void step(-1)}
          >
            −
          </button>
          <input
            type="number"
            min={1}
            className="ajout-n"
            aria-label={`Nombre de postes · ${row.name}`}
            value={amount}
            onChange={(e) => setAmount(Number.parseInt(e.target.value, 10) || 0)}
          />
          <button
            type="button"
            className="pas plus"
            aria-label={`Ajouter des postes livrés · ${row.name}`}
            disabled={adjust.isPending}
            onClick={() => void step(1)}
          >
            +
          </button>
        </span>
      </td>
      <td
        className={`reste${row.overDelivered ? " alerte" : ""}`}
        title={
          row.overDelivered
            ? `Incohérence : ${formatNumberFr(row.delivered)} postes livrés pour ${formatNumberFr(row.effectiveSeats)} déclarés.`
            : undefined
        }
      >
        {formatNumberFr(row.remaining)}
      </td>
    </tr>
  );
}

export function Stock() {
  const { data: me } = useMe();
  const { data: rows, isLoading } = useStock();
  const [search, setSearch] = useState("");
  const [lot, setLot] = useState("");
  const [zone, setZone] = useState("");
  const [sort, setSort] = useState("volume");

  const readOnly = me?.role !== "admin";

  const zones = useMemo(
    () => [...new Set((rows ?? []).map((r) => r.zone))].sort((a, b) => a.localeCompare(b, "fr")),
    [rows],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = (rows ?? []).filter(
      (r) =>
        (!needle || `${r.name} ${r.zone}`.toLowerCase().includes(needle)) &&
        (!lot || r.lot === lot) &&
        (!zone || r.zone === zone),
    );
    const cmp = (a: string, b: string) => a.localeCompare(b, "fr");
    return [...list].sort((a, b) => {
      switch (sort) {
        case "lieu":
          return cmp(a.zone, b.zone) || cmp(a.name, b.name);
        case "batiment":
          return cmp(a.name, b.name);
        case "livres":
          return b.delivered - a.delivered || cmp(a.name, b.name);
        case "reste":
          return b.remaining - a.remaining || cmp(a.name, b.name);
        default:
          return b.effectiveSeats - a.effectiveSeats || cmp(a.name, b.name);
      }
    });
  }, [rows, search, lot, zone, sort]);

  const totalRemaining = visible.reduce((s, r) => s + r.remaining, 0);
  const inconsistent = visible.filter((r) => r.overDelivered).length;

  return (
    <div className="gestion">
      <div className="admin-main">
        <div className="wrap">
          <section className="panel">
            <h1>Gestion des stocks</h1>
            <p className="lede">
              Catalogue des bâtiments et regroupements du planning, avec leur volume de postes.
            </p>

            <div className="note">
              {readOnly ? (
                <>
                  <b>Total livrés :</b> vous pouvez valider ou annuler des livraisons avec les
                  boutons − et + ci-dessous, au même titre que depuis « Mes livraisons ». Seul le
                  volume de postes prévus pour chaque bâtiment relève de l'exploitant SOI.
                </>
              ) : (
                <>
                  <b>Total livrés :</b> il correspond aux postes effectivement validés, que ce soit
                  par les intervenants depuis leur espace ou depuis cet écran. Les boutons − et +
                  modifient directement ces validations ; les livraisons saisies ici sont annulées
                  en premier, avant celles des intervenants.
                </>
              )}
            </div>

            <div className="toolbar">
              <input
                type="search"
                placeholder="Rechercher un bâtiment, une zone…"
                aria-label="Rechercher un lieu"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select aria-label="Filtrer par lot" value={lot} onChange={(e) => setLot(e.target.value)}>
                <option value="">Tous les lots</option>
                <option value="SOI">SOI</option>
                <option value="Police">Corps de Police</option>
              </select>
              <select
                aria-label="Filtrer par lieu"
                value={zone}
                onChange={(e) => setZone(e.target.value)}
              >
                <option value="">Tous les lieux</option>
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
              <label className="f-label" htmlFor="lieux-tri">
                Trier par
              </label>
              <select
                id="lieux-tri"
                aria-label="Trier le tableau"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="volume">Volume décroissant</option>
                <option value="lieu">Lieu (A → Z)</option>
                <option value="batiment">Bâtiment (A → Z)</option>
                <option value="livres">Postes livrés décroissant</option>
                <option value="reste">Reste à livrer décroissant</option>
              </select>
              <span className="muted" data-testid="stock-count">
                {formatNumberFr(visible.length)} bâtiment{visible.length > 1 ? "s" : ""} ·{" "}
                {formatNumberFr(totalRemaining)} postes à livrer
                {inconsistent > 0 &&
                  ` · ${formatNumberFr(inconsistent)} site${inconsistent > 1 ? "s" : ""} en sur-livraison`}
              </span>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                <div className="tbl-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Bâtiment</th>
                        <th>Lot</th>
                        <th>Lieu</th>
                        <th>Postes prévus</th>
                        <th>Postes réels</th>
                        <th>Total livrés</th>
                        <th>Ajuster</th>
                        <th>Reste</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && (
                        <tr>
                          <td colSpan={8}>Chargement…</td>
                        </tr>
                      )}
                      {visible.map((row) => (
                        <Row key={row.slug} row={row} readOnly={readOnly} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
